import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskText, EASE } from "@/components/Reveal";
import { HERO_IMAGE, HERO_IMAGE_ALT } from "@/data/site";

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-ivory" data-testid="hero">
      <div className="mx-auto grid max-w-[1500px] items-stretch gap-0 md:grid-cols-2">
        {/* Left copy */}
        <div className="relative z-10 flex flex-col justify-center px-6 py-20 md:px-14 md:py-32">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="eyebrow mb-8 text-gold"
          >
            Everyday Luxury Jewelry
          </motion.p>

          <MaskText
            lines={["Jewelry", "That Stays", "With You"]}
            className="font-serif text-[3.4rem] font-light leading-[0.95] tracking-tight text-ink sm:text-7xl lg:text-[5.5rem]"
            delay={0.35}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
            className="mt-8 max-w-md text-base leading-relaxed text-ink/70"
          >
            Timeless, everyday pieces made to shine through every moment.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/shop"
              className="rounded-full bg-ink px-10 py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ivory transition-transform duration-300 hover:scale-[1.03] hover:bg-gold"
              data-testid="hero-shop-now"
            >
              Shop Now
            </Link>
            <Link
              to="/collection/best-sellers"
              className="rounded-full border border-ink px-10 py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ink transition-colors duration-300 hover:bg-ink hover:text-ivory"
              data-testid="hero-explore"
            >
              Explore Collection
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 1 }}
            className="mt-14 text-[0.62rem] uppercase tracking-[0.28em] text-ink/50"
          >
            Anti-Tarnish · Everyday Wear · Timeless Style
          </motion.p>
        </div>

        {/* Right image with parallax */}
        <div className="relative min-h-[60vh] overflow-hidden md:min-h-[92vh]">
          <motion.div style={{ y, scale }} className="absolute inset-0 h-[115%]">
            <img
              src={HERO_IMAGE}
              alt="Woman wearing Noora Jewels"
              className="h-full w-full object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.4, duration: 1, ease: EASE }}
            className="absolute bottom-8 left-8 hidden max-w-[190px] rounded-2xl bg-ivory/85 p-5 backdrop-blur-md md:block"
          >
            <img src={HERO_IMAGE_ALT} alt="detail" className="mb-3 h-20 w-full rounded-lg object-cover" />
            <p className="font-serif text-lg leading-tight text-ink">The Everyday Edit</p>
            <p className="mt-1 text-xs text-ink/60">Pieces you'll never take off.</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
